import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  limit,
  orderBy,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useToast } from '../components/Toast';
import { logger } from '../utils/logger';

const ProjectsContext = createContext(null);

const PROJECTS_PAGE_SIZE = 50;

export function ProjectsProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();
  const { showToast } = useToast();

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsLastDoc, setProjectsLastDoc] = useState(null);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    if (!firebaseUser) return;
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;

    const q = query(
      collection(db, 'projects'),
      where('userId', '==', ownerIdToUse),
      orderBy('createdAt', 'desc'),
      limit(PROJECTS_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const projectsData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProjects(projectsData);
        setProjectsLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMoreProjects(snapshot.docs.length >= PROJECTS_PAGE_SIZE);
        setProjectsLoading(false);
      },
      (error) => {
        logger.error('Error loading projects:', error);
        setProjectsLoading(false);
        showToast('Failed to load projects. Please refresh the page.', 'error');
      }
    );

    return unsubscribe;
  }, [firebaseUser, teamOwnerId, showToast]);

  const loadMoreProjects = async () => {
    if (!projectsLastDoc || !hasMoreProjects) return;
    try {
      const ownerIdToUse = teamOwnerId || firebaseUser.uid;
      const q = query(
        collection(db, 'projects'),
        where('userId', '==', ownerIdToUse),
        orderBy('createdAt', 'desc'),
        startAfter(projectsLastDoc),
        limit(PROJECTS_PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const moreData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setProjects((prev) => [...prev, ...moreData]);
      setProjectsLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMoreProjects(snapshot.docs.length >= PROJECTS_PAGE_SIZE);
    } catch (error) {
      logger.error('Error loading more projects:', error);
      showToast('Failed to load more projects. Please try again.', 'error');
    }
  };

  const addProjectToFirebase = async (projectData) => {
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;
    try {
      const docRef = await addDoc(collection(db, 'projects'), {
        ...projectData,
        userId: ownerIdToUse,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      logger.error('Error adding project:', error);
      return { success: false, error: error.message };
    }
  };

  const updateProjectInFirebase = async (projectId, updates) => {
    try {
      await updateDoc(doc(db, 'projects', projectId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      logger.error('Error updating project:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteProjectFromFirebase = async (projectId) => {
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      return { success: true };
    } catch (error) {
      logger.error('Error deleting project:', error);
      return { success: false, error: error.message };
    }
  };

  // Transition a project to a new phase and status atomically
  const transitionPhase = async (projectId, newPhase, newStatus) => {
    return updateProjectInFirebase(projectId, { phase: newPhase, status: newStatus });
  };

  const value = useMemo(
    () => ({
      projects,
      setProjects,
      projectsLoading,
      addProjectToFirebase,
      updateProjectInFirebase,
      deleteProjectFromFirebase,
      transitionPhase,
      selectedProject,
      setSelectedProject,
      loadMoreProjects,
      hasMoreProjects,
      // CRUD functions (addProjectToFirebase etc.) are stable useCallback refs — intentionally omitted from deps
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [projects, projectsLoading, selectedProject, hasMoreProjects]
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (!context) throw new Error('useProjects must be used within ProjectsProvider');
  return context;
}
